import { del, get, patch, post } from '@agribot/polygon-checks-ui'
import type { SupplierDocument } from '@agribot/sourcing-model'
import type { ScenarioRun } from './scenario'
import type { ReviewStore } from './store'
import type { Dashboard, ExportFormat, ExportJob, ExportLevel, GroupStructure, OriginSupply, Queue, ReviewSubmission, SupplyPlot } from './types'

/**
 * The review screens, dashboard, structure diagrams, scenarios and exports
 * against eudr-api. The review routes answer in this package's own shapes,
 * so each method is one call; the session (Auth0 cookie or token) is the UI
 * package's client's, as for every other screen.
 */
export function createApiReviewStore(): ReviewStore {
  // The campaign the dashboard shows is the one exports and scenarios read.
  let campaignId: string | null = null
  const campaign = async () => {
    if (!campaignId) campaignId = (await get<Dashboard>('/dashboard')).campaign.id
    return campaignId
  }

  return {
    queue: () => get<Queue>('/review/queue'),

    async open(id) {
      const s = await get<ReviewSubmission & { canAct?: boolean }>(`/review/submissions/${id}`)
      // Opening it at your own level picks it up, as the queue promises.
      if (s.canAct && (s.status === 'submitted' || s.status === 'validated')) {
        await patch(`/review/submissions/${id}`, { inReview: true })
        return get<ReviewSubmission>(`/review/submissions/${id}`)
      }
      return s
    },

    addFlag: async (id, target, note) => { await post(`/review/submissions/${id}/flags`, { target, note }) },
    removeFlag: async (_id, flagId) => { await del(`/review/flags/${flagId}`) },
    approve: async (id, comment) => { await post(`/review/submissions/${id}/decisions`, { decision: 'approve', ...(comment ? { comment } : {}) }) },
    returnToSupplier: async (id, comment) => { await post(`/review/submissions/${id}/decisions`, { decision: 'return', comment }) },

    async dashboard() {
      const d = await get<Dashboard>('/dashboard')
      campaignId = d.campaign.id
      return d
    },

    originSupply: (originId) => get<OriginSupply>(`/origins/${originId}/supply`),
    supplyPlots: (originId, filter) => {
      const q = new URLSearchParams({ limit: '100', ...Object.fromEntries(Object.entries(filter).filter(([, v]) => v !== undefined && v !== '').map(([k, v]) => [k, String(v)])) })
      return get<{ plots: SupplyPlot[]; total: number }>(`/origins/${originId}/supply/plots?${q}`)
    },
    supplyGeojson: (originId) => get<GeoJSON.FeatureCollection>(`/origins/${originId}/supply/plots.geojson`),
    documents: (id) => get<{ documents: SupplierDocument[] }>(`/review/submissions/${id}/documents`).then((r) => r.documents),

    structure: (parentCompanyId) => get<GroupStructure>(`/parent-companies/${parentCompanyId}/structure`),

    scenario: (campaignId, shocks, includeDrafts) => post<ScenarioRun>('/scenario-runs', { campaignId, shocks, includeDrafts: Boolean(includeDrafts) }),

    exportFormats: ['xlsx', 'csv'] as ExportFormat[],
    exportLevels: ['origins', 'plots'] as ExportLevel[],
    startExport: async (format, level = 'origins') => post<ExportJob>(`/campaigns/${await campaign()}/exports`, { format, level }),
    exportJob: (id) => get<ExportJob>(`/exports/${id}`),
  }
}
